import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag, User, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useHybridCart } from "@/contexts/HybridCartContext";
import { formatCurrency } from "@/lib/utils";
import { gtmEvent, mapItemToGA4 } from "@/lib/gtm";
import { pixelViewCart } from "@/lib/pixel";

const Cart = () => {
  const navigate = useNavigate();
  const {
    cart,
    loading,
    updateItemQuantity,
    removeItem,
    clearCart,
    totalItems,
    subtotal,
    isGuest,
    getCartItems,
  } = useHybridCart();

  const cartItems = getCartItems();

  // GTM + Pixel: view_cart
  useEffect(() => {
    if (loading || !cartItems.length) return;
    gtmEvent("view_cart", {
      ecommerce: {
        currency: "USD",
        value: subtotal,
        items: cartItems.map((item) => mapItemToGA4({
          id: "productId" in item ? item.productId : item.product?._id ?? "",
          name: "name" in item ? item.name : item.product?.title ?? "",
          price: "price" in item ? item.price : item.product?.price ?? 0,
          quantity: item.quantity,
        })),
      },
    });
    pixelViewCart({ value: subtotal, num_items: cartItems.length });
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FEFDFD] pb-12">
        <div className="container mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!cart || totalItems === 0) {
    return (
      <div className="min-h-screen bg-[#FEFDFD] pb-12">
        <div className="container mx-auto px-4 max-w-md text-center">
          <Card>
            <CardContent className="pt-8 pb-6">
              <ShoppingBag className="w-16 h-16 mx-auto text-brown/50 mb-4" />
              <h2 className="text-xl font-bold text-brown-dark mb-2">
                Tu carrito está vacío
              </h2>
              <p className="text-brown mb-6">
                ¡Explora nuestros productos artesanales y encuentra algo
                especial!
              </p>
              <Button onClick={() => navigate("/products")} className="w-full">
                Ver productos
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleQuantityChange = async (
    productId: string,
    newQuantity: number
  ) => {
    if (newQuantity < 1) return;
    try {
      await updateItemQuantity(productId, newQuantity);
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const handleRemoveItem = async (productId: string) => {
    try {
      const item = cartItems.find((i) =>
        "productId" in i ? i.productId === productId : i.product?._id === productId
      );
      if (item) {
        gtmEvent("remove_from_cart", {
          ecommerce: {
            currency: "USD",
            items: [mapItemToGA4({
              id: productId,
              name: "name" in item ? item.name : item.product?.title ?? "",
              price: "price" in item ? item.price : item.product?.price ?? 0,
              quantity: item.quantity,
            })],
          },
        });
      }
      await removeItem(productId);
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  const proceedToCheckout = () => {
    navigate("/checkout");
  };

  return (
    <div className="min-h-screen bg-[#FEFDFD] pb-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="text-3xl font-bold text-brown-dark">Mi Carrito</h1>
            <div className="flex items-center gap-2 mt-2">
              {isGuest ? (
                <Badge
                  variant="outline"
                  className="bg-amber-50 text-amber-700 border-amber-200"
                >
                  <User className="w-3 h-3 mr-1" />
                  Carrito local
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-200"
                >
                  <UserPlus className="w-3 h-3 mr-1" />
                  Cuenta vinculada
                </Badge>
              )}
              <span className="text-brown">
                {totalItems} producto{totalItems !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {cartItems.length > 0 && (
            <Button
              variant="outline"
              onClick={handleClearCart}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Vaciar carrito
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => {
              // Handle both guest and user cart items
              // For user cart: item.product can be either an ObjectId string or a populated object
              // For guest cart: item.productId is always present
              const productId =
                "productId" in item
                  ? item.productId
                  : typeof item.product === 'string'
                    ? item.product // Agenda personalizada (string ID)
                    : item.product._id; // Producto normal (objeto populado)

              const productName =
                "name" in item
                  ? item.name
                  : typeof item.product === 'string'
                    ? 'Agenda Personalizada 2026' // Agenda personalizada
                    : item.product.name || item.product.title;

              const productPrice =
                "price" in item
                  ? item.price
                  : typeof item.product === 'string'
                    ? 35.00 // Precio fijo para agendas
                    : item.product.price;

              const productImage =
                "image" in item
                  ? item.image
                  : typeof item.product === 'string'
                    ? null // Agendas no tienen imagen en el carrito de usuario
                    : item.product.image;

              // Debug log para ver qué está pasando con las imágenes
              console.log('🖼️ Cart item image debug:', {
                productId,
                productName,
                hasImageInItem: "image" in item,
                imageValue: item.image ? `${item.image.substring(0, 50)}...` : 'NO IMAGE',
                finalProductImage: productImage ? `${productImage.substring(0, 50)}...` : 'NO IMAGE'
              });

              const quantity = item.quantity;
              const itemSubtotal =
                "subtotal" in item ? item.subtotal : productPrice * quantity;

              return (
                <Card key={productId} className="overflow-hidden">
                  <CardContent className="p-4 sm:p-6">
                    {/* Fila superior: imagen + nombre + eliminar */}
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-lg overflow-hidden">
                        {productImage ? (
                          <img
                            src={productImage}
                            alt={productName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                            Sin imagen
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-brown-dark text-sm sm:text-base leading-tight">
                          {productName}
                        </h3>
                        <p className="text-brown text-sm mt-0.5">
                          {formatCurrency(productPrice)}
                        </p>
                        {item.customization?.personalizedName && (
                          <div className="text-xs text-primary font-medium mt-1 flex items-center gap-1">
                            <span>⭐</span>
                            <span className="truncate">"{item.customization.personalizedName}"</span>
                          </div>
                        )}
                        {item.customization?.notes && (
                          <div className="text-xs text-brown/70 mt-0.5 flex items-center gap-1">
                            <span>📝</span>
                            <span className="truncate">{item.customization.notes}</span>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(productId)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 min-h-[40px] min-w-[40px] touch-manipulation flex-shrink-0"
                        aria-label="Eliminar del carrito"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Fila inferior: cantidad + subtotal */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuantityChange(productId, quantity - 1)}
                          disabled={quantity <= 1}
                          className="w-9 h-9 p-0 touch-manipulation"
                          aria-label="Disminuir cantidad"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </Button>
                        <span className="w-8 text-center font-medium text-sm">
                          {quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuantityChange(productId, quantity + 1)}
                          className="w-9 h-9 p-0 touch-manipulation"
                          aria-label="Aumentar cantidad"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </div>

                      <p className="font-semibold text-brown-dark text-sm sm:text-base">
                        {formatCurrency(itemSubtotal)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:sticky lg:top-32 h-fit">
            <Card>
              <CardHeader>
                <CardTitle>Resumen del pedido</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>

                <div className="flex justify-between">
                  <span>Envío</span>
                  <span className="text-sm text-brown">
                    Se le indicará por WhatsApp
                  </span>
                </div>

                <hr />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>

                {/* {isGuest && (
                  <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                    💡 Los impuestos y costos de envío se calcularán en el
                    checkout
                  </div>
                )} */}

                {/* {!isAuthenticated && (
                  <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
                    💡 Crea una cuenta para obtener envío gratis y descuentos
                    exclusivos
                  </div>
                )} */}
              </CardContent>

              <CardFooter className="flex flex-col space-y-3">
                <Button
                  onClick={proceedToCheckout}
                  className="w-full bg-primary hover:bg-primary-dark text-brown-dark min-h-[48px] touch-manipulation active:scale-[0.98]"
                  size="lg"
                >
                  Proceder al checkout
                </Button>

                <Button
                  variant="outline"
                  onClick={() => navigate("/products")}
                  className="w-full min-h-[44px] touch-manipulation"
                >
                  Continuar comprando
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
